import { z } from 'zod';

import {
  nameRegex,
  validateDateOfBirth,
  validatePhoneNumber,
  validateReferrerInformation,
} from './utils';

const phoneNumberType = z.string().superRefine(validatePhoneNumber);

export const memberInfoSchema = z
  .object({
    firstName: z
      .string()
      .trim()
      .max(36, { message: 'Maximum 36 characters allowed' })
      .min(1, { message: 'First name is required' })
      .regex(nameRegex, {
        message: "First name can't contain special characters",
      }),
    lastName: z
      .string()
      .trim()
      .max(36, { message: 'Maximum 36 characters allowed' })
      .min(1, { message: 'Last name is required' })
      .regex(nameRegex, { message: "Last name can't contain special characters" }),
    preferredName: z
      .string()
      .max(36, { message: 'Maximum 36 characters allowed' })
      .optional()
      .nullable()
      .refine(
        (name) => {
          if (!name) return true;
          return nameRegex.test(name);
        },
        { message: "Preferred name can't contain special characters" },
      ),
    gender: z.enum(['male', 'female']),
    dob: z.string().trim().nullable().superRefine(validateDateOfBirth),
    email: z
      .string()
      .trim()
      .min(1, { message: 'Email is required' })
      .max(254, { message: 'Email cannot exceed 254 characters' })
      .email('Invalid email address'),
    phoneNumber: phoneNumberType,
    clinicalConsent: z.boolean().refine((value) => value, { message: 'Required' }),
    termsConsent: z.boolean().refine((value) => value, { message: 'Required' }),
    cancellationPolicyConsent: z.boolean().refine((value) => value, { message: 'Required' }),
    medicalReferral: z.string(),
    referrerName: z
      .string()
      .trim()
      .regex(nameRegex, {
        message: "Referral name can't contain special characters",
      })
      .optional(),
    referrerPhoneNumber: phoneNumberType.optional(),
  })
  .superRefine(validateReferrerInformation);

export const phoneNumberSchema = z.object({
  phoneNumber: phoneNumberType,
});

export type MemberInfo = z.infer<typeof memberInfoSchema>;
